import Debug from '@/util/debug';
import Promise from 'bluebird';
import { findUrlsInText, isOfflineModeSupported, checkStorage } from '@/util/helper';
import offlineCheck from '@/mixins/offline-check';
import { MAP_OFFLINE_ZOOM, IDB_TABLE_LOCATIONS, SW_DB_KEYS } from '@/util/constants';
import idb from '@/util/indexed-db';
import * as LocalStorage from '@/util/local-storage';
import { changeStatus } from '@/util/sw-messages';
import Switches from 'vue-switches';
import moment from 'moment-timezone';

const debug = Debug('offline-mode');
// TODO - import font url
const fontUrl = 'https://fonts.googleapis.com/css?family=Open+Sans:400,400i,600,600i,700,700i&amp;subset=cyrillic,greek-ext';
const offlineLocationLimit = 1500;
const downloadTypes = {
  fetch: 'fetch',
  service: 'service',
  raw: 'raw',
};

export default {
  dependencies: [
    'adApiService',
    'draftService',
    'publicConfigService',
  ],
  components: {
    Switches,
  },
  mixins: [offlineCheck],
  data() {
    return {
      config: {},
      downloading: false,
      downloads: {
        total: 0,
        completed: 0,
      },
    };
  },
  computed: {
    offlineSupported() {
      return isOfflineModeSupported();
    },
    disableDownloadBtn() {
      return this.downloading || !this.isOnline || !this.offlineSupported || !this.realOnline;
    },
  },
  mounted() {
    // Check if user is offline files are up-to-date
    if (this.offlineSupported) {
      this.isUserFilesUpToDate()
        .then(upToDate => {
          if (!upToDate) {
            // Remove last updated for SW to know that user have outdated files
            LocalStorage.remove(SW_DB_KEYS.lastFileUpdate);
          }
        });
    }
  },
  methods: {
    showModal(show = true) {
      if (show) this.$refs.offlineModeModal.show();
      else this.$refs.offlineModeModal.hide();
    },
    startDownload(e) {
      e.cancel();
      this.checkIsStoragePersisted()
        .then(success => {
          if (success) {
            this.downloading = true;
            this.downloads.completed = 0;
            this.downloads.total = 0;

            this.publicConfigService.getConfig()
              .then(c => this.config = c)
              // Remove last file update (as some downloads can fail for some reason)
              .then(() => LocalStorage.remove(SW_DB_KEYS.lastFileUpdate))
              .then(() => {
                // Download all necessary stuff
                Promise.all([
                  this.getLocations(offlineLocationLimit)
                    .then(urls => this.downloadResources(urls, downloadTypes.service, false)),
                  this.getMapTileUrls(MAP_OFFLINE_ZOOM)
                    .then(urls => this.downloadResources(urls, downloadTypes.fetch)),
                  this.getResourceUrls()
                    .then(urls => this.downloadResources(urls, downloadTypes.service)),
                  this.getOtherServices()
                    .then(urls => this.downloadResources(urls, downloadTypes.service, false)),
                  this.getFontUrls()
                    .then(urls => this.downloadResources(urls, downloadTypes.fetch)),
                ])
                  .then(() => this.getLastUpdatedAt())
                  .then(date => {
                    const lastUpdated = date === null ? moment().valueOf() : date;
                    return LocalStorage.set(SW_DB_KEYS.lastFileUpdate, lastUpdated);
                  })
                  .then(() => {
                    this.downloading = false;
                    this.$toast.success({ message: this.$t('pos.offline.offline-files-download-completed') });
                  })
                  .catch(err => {
                    this.downloading = false;
                    this.downloads.completed = 0;
                    this.downloads.total = 0;
                    debug.warn('error while downloading files %o', err);
                    this.$toast.error({ message: this.$t('pos.offline.error-while-downloading-files') });
                  });
              });
          } else {
            this.$toast.error({ message: this.$t('pos.offline.enable-persistent-storage') });
          }
        });
    },
    checkIsStoragePersisted() {
      if (checkStorage()) {
        return navigator.storage.persisted()
          .then(persisted => {
            if (persisted) {
              return true;
            }
            return navigator.storage.persist();
          });
      }
      return Promise.resolve(false);
    },
    getFontUrls() {
      return fetch(fontUrl)
        .then(response => response.text())
        .then(text => findUrlsInText(text))
        .catch(err => {
          debug.warn('Fetch Error :-S', err);
        });
    },
    getMapTileUrls(zoomUntil) {
      const urls = [];
      for (let z = 0; z <= zoomUntil; z += 1) {
        const pow = 2 ** z;
        for (let x = 0; x < pow; x += 1) {
          for (let y = 0; y < pow; y += 1) {
            // TODO - load url from public or generic service
            const url = `https://gotu-offline-maps-tests.s3.amazonaws.com/tiles/${z}/${x}/${y}.png`;
            urls.push(url);
          }
        }
      }
      return Promise.resolve(urls);
    },
    getResourceUrls() {
      return Promise.resolve([
        this.adApiService.getStatusFlows(),
        this.adApiService.getStatuses(),
        this.adApiService.getInterests(),
        this.adApiService.getLocales(),
        this.adApiService.getClusters(),
        this.adApiService.getPackages(),
        this.adApiService.getAges(),
        this.adApiService.getRadiuses(),
        this.adApiService.getAdExamples(),
        this.adApiService.getAdvantages(),
        this.adApiService.getPostExamples(),
        this.adApiService.getCreationModes(),
      ]);
    },
    getLocations(limit) {
      return idb[IDB_TABLE_LOCATIONS].clear()
        .then(() => this.adApiService.getResourceCount())
        .then(resources => {
          const urls = [];
          if (resources.offline_locations) {
            for (let offset = 0; offset < resources.offline_locations; offset += limit) {
              urls.push(
                this.adApiService.getOfflineLocations(limit, offset)
                  .then(res => idb[IDB_TABLE_LOCATIONS].bulkPut(res)),
              );
            }
          }
          return urls;
        });
    },
    getOtherServices() {
      return Promise.all([
        this.draftService.getDrafts('saved'),
        this.draftService.getDrafts('synced'),
      ])
        .then(res => {
          const promises = [
            this.adApiService.getCurrentUser(),
            this.adApiService.getPublicConfig(),
            this.adApiService.getGenericConfig(),
          ];
          const drafts = [];
          res.forEach(r => {
            if (r.length > 0) drafts.push(...r);
          });

          drafts.forEach(d => {
            promises.push(this.adApiService.getDraft(d.id));
          });

          return promises;
        });
    },
    downloadResources(urls, type, processUrls = true) {
      if (urls.length > 0) {
        this.downloads.total += urls.length;

        switch (type) {
          case downloadTypes.service:
            return Promise.all(urls)
              .then(response => (processUrls
                  ? this.processResponseUrls(response)
                  : Promise.resolve())
                    .then(() =>
                      (this.downloading ? this.downloads.completed += urls.length : null),
                    ),
              );
          default:
            return Promise.map(urls, url => (type === downloadTypes.fetch ? window.fetch(url) : this.adApiService.rawRequest('GET', url))
              .then(response => (processUrls
                ? this.processResponseUrls(response)
                : Promise.resolve()),
              )
              .then(() =>
                (this.downloading ? this.downloads.completed += 1 : null),
              ), { concurrency: 5 });
        }
      }

      return Promise.resolve();
    },
    processResponseUrls(response) {
      const urls = findUrlsInText(JSON.stringify(response));
      return new Promise(resolve => {
        if (urls.length > 0) {
          return resolve(this.downloadResources(urls, downloadTypes.fetch));
        }
        return resolve();
      });
    },
    getLastUpdatedAt() {
      return this.adApiService.getLastUpdate()
        .then(result => Object.keys(result).reduce((acc, date) => {
          if (date !== null && (acc === null || moment(result[date]).isAfter(acc))) {
            return result[date];
          }
          return acc;
        }, null));
    },
    isUserFilesUpToDate() {
      return LocalStorage.get(SW_DB_KEYS.lastFileUpdate, null)
        .then(lastUpdate => {
          // User has downloaded files in past
          if (lastUpdate !== null) {
            return this.getLastUpdatedAt().then(date => moment(date).isSame(lastUpdate));
          }
          return Promise.resolve(false);
        });
    },
    onSwitch() {
      // VueSwitches uses e.target.checked instead of some internal value
      changeStatus(!this.isOnline).catch(err => debug.info('ignored', err));
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/offline-mode/OfflineMode.js